/**
 * Move-by-move review of a finished game.
 *
 * Replays a move log through the interpreter, searches every position the log
 * passed through, and says four things about each move: what it scored, what
 * the engine would have played, how much the difference was worth, and **why**
 * — which threat was missed, which one was handed over, what the better move
 * would have set up. The first three are numbers off the search; the fourth is
 * proven separately by replaying the position, because a score can rank a move
 * but cannot explain one.
 *
 * There is deliberately **no accuracy percentage**. A single headline number
 * would have to be invented — the formula the chess sites use is theirs and is
 * not published — and an invented number is a lie with a decimal point. What is
 * reported instead is countable and checkable: which moves matched the engine,
 * and how many of each kind of error there were.
 *
 * Pure, like the rest of `games/`: the only impurity available is the clock the
 * caller passes in for the search budget.
 */
import { applyMove, boardSize, createInitial } from '../spec/interpreter.ts';
import type { BoardOf, Move, SeatOf, State } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';
import { WINDOW_SCALE, longestWinLength, windowWeight } from './eval.ts';
import { explainMove } from './reasons.ts';
import type { MoveReasoning } from './reasons.ts';
import { mateDistance, outcomeOf, searchPosition } from './search.ts';
import type { MoveScore, Outcome } from './search.ts';

/**
 * How good a move was. Ordered from best to worst; the client renders the same
 * six words, so this list is the vocabulary of the whole feature.
 */
export const VERDICTS = ['best', 'only', 'good', 'inaccuracy', 'mistake', 'blunder'] as const;
export type Verdict = (typeof VERDICTS)[number];

/** Verdicts that mean the move cost something worth pointing at. */
export function isError(verdict: Verdict): boolean {
  return verdict === 'inaccuracy' || verdict === 'mistake' || verdict === 'blunder';
}

export interface MoveReview {
  /** Index into the board-move log, 0-based. */
  readonly ply: number;
  /** The seat that played it. */
  readonly seat: number;
  /** The move parameter that was played — a column, in the current vocabulary. */
  readonly param: number;
  /** The board cell the played move filled, so the client can point at it. */
  readonly cell: number;
  readonly verdict: Verdict;
  /** Score of the played move, from the mover's point of view. */
  readonly playedScore: number;
  /** Score of the engine's choice, same point of view. */
  readonly bestScore: number;
  /** `bestScore - playedScore`, never negative. */
  readonly loss: number;
  /** The engine's move, and where its piece would have landed. */
  readonly bestParam: number;
  readonly bestCell: number;
  readonly playedOutcome: Outcome;
  readonly bestOutcome: Outcome;
  /** Plies to the win the engine's move forces, when it forces one. */
  readonly bestMateIn: number | null;
  /** Plies to the loss the played move allows, when it allows one. */
  readonly playedMateIn: number | null;
  /** How many legal moves there were. `1` makes the verdict a formality. */
  readonly legalMoves: number;
  /** Deepest completed search iteration for this position. */
  readonly depth: number;
  /**
   * Proven tactical facts about the played move and the engine's move.
   *
   * Plain data, because this crosses a worker's `postMessage` boundary.
   */
  readonly reasoning: MoveReasoning;
}

/** A truthful count, per seat. No composite score, because none is honest. */
export interface SeatTally {
  readonly seat: number;
  readonly moves: number;
  /** Moves that matched the engine's top score, ties included. */
  readonly matched: number;
  readonly good: number;
  readonly inaccuracies: number;
  readonly mistakes: number;
  readonly blunders: number;
}

export interface GameReview {
  readonly entries: readonly MoveReview[];
  readonly tallies: readonly SeatTally[];
  /** True when every position was searched to a proven result. */
  readonly solved: boolean;
}

/**
 * Wall-clock milliseconds per position, chosen by measurement.
 *
 * A full 42-move Connect 4 game reviews in about 1.4s at this budget, median
 * search depth 9 and every endgame solved outright — fast enough that the
 * screen is useful before the loser has finished swearing, deep enough that the
 * word "blunder" is earned. Raising it to 100ms bought one ply for another
 * second, which is a bad trade on a phone.
 */
export const DEFAULT_POSITION_BUDGET_MS = 60;

export interface ReviewOptions {
  /** Wall-clock milliseconds per position. Defaults to `DEFAULT_POSITION_BUDGET_MS`. */
  readonly budgetMs?: number;
  readonly maxDepth?: number;
  readonly now?: () => number;
  /**
   * Called after each ply, so a worker can stream progress instead of going
   * quiet for several seconds. Must not throw.
   */
  readonly onEntry?: (entry: MoveReview, total: number) => void;
}

/**
 * The eval loss that counts as serious, derived from the spec.
 *
 * One window one piece short of winning — three-in-a-four for Connect 4, four
 * -in-a-five for a longer game. Scaling off the spec is what stops "a mistake"
 * from meaning something different on every board.
 */
export function seriousLossUnit(rules: RulesSpec): number {
  const length = longestWinLength(rules);
  return windowWeight(Math.max(1, length - 1)) * WINDOW_SCALE;
}

export interface ClassifyInput {
  readonly playedScore: number;
  readonly bestScore: number;
  readonly playedOutcome: Outcome;
  readonly bestOutcome: Outcome;
  /** Score of the best move that is not the engine's first choice, if any. */
  readonly runnerUpOutcome: Outcome | null;
  readonly legalMoves: number;
  readonly unit: number;
}

/** Win beats draw-or-unknown beats loss. Unknown sits with draw: it is "not proven bad". */
function outcomeRank(outcome: Outcome): number {
  if (outcome === 'win') return 2;
  if (outcome === 'loss') return 0;
  return 1;
}

/**
 * Turn two scores into one word.
 *
 * Outcome beats eval whenever the outcome is proven: losing a won game is a
 * blunder however small the numeric drop, and a 200-point slide inside a
 * position that is drawn either way is not.
 */
export function classifyMove(input: ClassifyInput): Verdict {
  const { playedScore, bestScore, playedOutcome, bestOutcome, unit } = input;

  if (playedScore >= bestScore) {
    // The single move that keeps the result: worth its own word, because "best"
    // undersells a position where everything else throws the game away.
    const onlyMove =
      input.legalMoves > 1 &&
      input.runnerUpOutcome !== null &&
      outcomeRank(bestOutcome) > outcomeRank(input.runnerUpOutcome);
    return onlyMove ? 'only' : 'best';
  }

  // Walked into a proven loss that was avoidable.
  if (playedOutcome === 'loss' && bestOutcome !== 'loss') return 'blunder';

  if (bestOutcome === 'win' && playedOutcome !== 'win') {
    // A proven win traded for a proven draw is the textbook mistake; a proven
    // win traded for a position the engine cannot even hold is worse.
    return playedOutcome === 'draw' ? 'mistake' : 'blunder';
  }

  const loss = bestScore - playedScore;
  if (loss <= unit * 0.5) return 'good';
  if (loss <= unit * 1.5) return 'inaccuracy';
  if (loss <= unit * 4) return 'mistake';
  return 'blunder';
}

function countEmpty<S extends RulesSpec>(rules: S, board: BoardOf<S>): number {
  const size = boardSize(rules);
  let empty = 0;
  for (let index = 0; index < size; index += 1) {
    if (board[index] === rules.cells.empty) empty += 1;
  }
  return empty;
}

function scoreOf(moves: readonly MoveScore[], param: number): number | null {
  for (const entry of moves) {
    if (entry.param === param) return entry.score;
  }
  return null;
}

/**
 * Review one move, and hand back the position it leads to.
 *
 * Extracted from `reviewGame` so the live coach can judge the move a player has
 * just made without replaying and re-searching the whole game behind it. Both
 * callers therefore classify moves through exactly the same code: a coach that
 * called a move a mistake during the game and an inaccuracy on the review
 * screen afterwards would discredit both screens at once.
 *
 * Returns null when the move is not playable, or when the search has no opinion
 * because the position is already over.
 */
export interface ReviewedMove<S extends RulesSpec> {
  readonly entry: MoveReview;
  /** The position the move leads to, so a caller can keep replaying. */
  readonly next: State<S>;
  /**
   * True when the search reached a proven result rather than stopping on the
   * clock. Only `reviewGame`'s `solved` flag needs it, but it cannot be
   * recovered afterwards from the entry alone.
   */
  readonly exact: boolean;
}

export function reviewMove<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  ply: number,
  move: Move,
  options: ReviewOptions = {},
): ReviewedMove<S> | null {
  if (state.status.kind !== 'in_play') return null;

  const seat = state.toMove;
  const applied = applyMove(rules, state, seat, move);
  if (!applied.ok) return null;

  const search = searchPosition(rules, state, {
    budgetMs: options.budgetMs ?? DEFAULT_POSITION_BUDGET_MS,
    ...(options.maxDepth === undefined ? {} : { maxDepth: options.maxDepth }),
    ...(options.now === undefined ? {} : { now: options.now }),
  });

  const best = search.moves[0];
  const playedScore = scoreOf(search.moves, move.param);
  if (best === undefined || playedScore === null) return null;

  // Exactness matters for one thing only: telling a proven draw from a position
  // that merely evaluates to zero.
  const exact = search.completed && search.depth >= countEmpty(rules, state.board);
  const runnerUp = search.moves.find((entry) => entry.score < best.score) ?? null;
  const bestApplied = applyMove(rules, state, seat, { param: best.param });
  const reasoning = explainMove(rules, state, seat, move, { param: best.param });

  const entry: MoveReview = {
    ply,
    seat,
    param: move.param,
    cell: applied.primary,
    verdict: classifyMove({
      playedScore,
      bestScore: best.score,
      playedOutcome: outcomeOf(playedScore, exact),
      bestOutcome: outcomeOf(best.score, exact),
      runnerUpOutcome: runnerUp === null ? null : outcomeOf(runnerUp.score, exact),
      legalMoves: search.moves.length,
      unit: seriousLossUnit(rules),
    }),
    playedScore,
    bestScore: best.score,
    loss: Math.max(0, best.score - playedScore),
    bestParam: best.param,
    bestCell: bestApplied.ok ? bestApplied.primary : -1,
    playedOutcome: outcomeOf(playedScore, exact),
    bestOutcome: outcomeOf(best.score, exact),
    bestMateIn: best.score > 0 ? mateDistance(best.score) : null,
    playedMateIn: playedScore < 0 ? mateDistance(playedScore) : null,
    legalMoves: search.moves.length,
    depth: search.depth,
    reasoning,
  };

  return { entry, next: applied.state, exact };
}

/**
 * Review every board move in `moves`.
 *
 * The log carries board moves only: a resignation ends a match without touching
 * the board, so there is nothing about it for an engine to have an opinion on.
 * Callers strip those rows before getting here.
 *
 * A log that the interpreter rejects is reviewed up to the offending move and
 * no further, rather than throwing — a corrupt tail must not make the honest
 * prefix unreadable.
 */
export function reviewGame<S extends RulesSpec>(
  rules: S,
  firstSeat: SeatOf<S>,
  moves: readonly Move[],
  options: ReviewOptions = {},
): GameReview {
  const entries: MoveReview[] = [];
  let state: State<S> = createInitial(rules, firstSeat);
  let solved = true;

  for (const [ply, move] of moves.entries()) {
    // A move the interpreter rejects, or a search with no opinion, ends the
    // review here: a corrupt tail must not make the honest prefix unreadable.
    const reviewed = reviewMove(rules, state, ply, move, options);
    if (reviewed === null) break;

    if (!reviewed.exact) solved = false;

    entries.push(reviewed.entry);
    options.onEntry?.(reviewed.entry, moves.length);
    state = reviewed.next;
  }

  return { entries: Object.freeze(entries), tallies: tally(rules, entries), solved };
}

/** One row per seat the spec declares, so a seat that never erred still reports zero. */
function tally<S extends RulesSpec>(rules: S, entries: readonly MoveReview[]): readonly SeatTally[] {
  return Object.freeze(
    rules.seats.map((seat) => {
      const own = entries.filter((entry) => entry.seat === seat);
      const count = (verdict: Verdict): number =>
        own.filter((entry) => entry.verdict === verdict).length;
      return Object.freeze({
        seat,
        moves: own.length,
        matched: count('best') + count('only'),
        good: count('good'),
        inaccuracies: count('inaccuracy'),
        mistakes: count('mistake'),
        blunders: count('blunder'),
      });
    }),
  );
}
